/**
 * chat-director/budget — token-budget accounting + graceful breadth degradation.
 *
 * A large, very chatty, LLM-driven world costs real tokens. As the daily budget
 * tightens we DEGRADE BREADTH gracefully — fewer characters get fresh AI lines,
 * the TemplateVoice floor quietly covers the rest — rather than snapping the whole
 * town to canned lines. Bias toward freshness, but never overspend.
 */
import type { Beat, BeatPriority, BudgetState } from '@/shared';

/** Per-batch token estimate used to gate LLM spend before a call is made. */
export const TOKENS_PER_LINE_ESTIMATE = 60;
/** Fixed prompt overhead charged to every LLM batch (personas + system). */
export const BATCH_OVERHEAD_TOKENS = 200;

/**
 * Headroom fraction at which a beat priority is still allowed a fresh AI line.
 * As `tokensSpentToday / dailyTokenBudget` climbs, lower-priority beats fall back
 * to templates first; highlights keep their fresh voice longest.
 */
export const LLM_HEADROOM_THRESHOLD: Readonly<Record<BeatPriority, number>> = {
  // Allowed while remaining headroom is at least this fraction of the budget.
  ambient: 0.5, // ambient banter drops to templates first
  normal: 0.2,
  highlight: 0.02, // loud, memorable moments keep a fresh voice nearly to the end
};

/** True once the day's tokens are exhausted. */
export function isOverBudget(state: BudgetState): boolean {
  return state.overBudget || state.tokensSpentToday >= state.dailyTokenBudget;
}

/** Remaining tokens today (never negative). */
export function remainingTokens(state: BudgetState): number {
  return Math.max(0, state.dailyTokenBudget - state.tokensSpentToday);
}

/** Fraction of the daily budget still unspent (0…1). */
export function headroomFraction(state: BudgetState): number {
  if (state.dailyTokenBudget <= 0) return 0;
  return remainingTokens(state) / state.dailyTokenBudget;
}

/** Estimated tokens a batch of this many lines would cost via the LLM. */
export function estimateBatchTokens(lineCount: number): number {
  return BATCH_OVERHEAD_TOKENS + Math.max(1, lineCount) * TOKENS_PER_LINE_ESTIMATE;
}

/**
 * Decide whether a batch (summarized by its loudest beat priority + line count)
 * should get a FRESH LLM line vs the template floor.
 *
 * - Over budget ⇒ always template.
 * - Not enough headroom to afford the batch ⇒ template (never overspend).
 * - Otherwise gate by priority headroom so breadth degrades from the bottom up.
 */
export function shouldUseLlmForBatch(
  loudest: BeatPriority,
  lineCount: number,
  state: BudgetState,
): boolean {
  if (isOverBudget(state)) return false;
  if (estimateBatchTokens(lineCount) > remainingTokens(state)) return false;
  return headroomFraction(state) >= LLM_HEADROOM_THRESHOLD[loudest];
}

/** Whether a single beat (its priority) currently warrants a fresh AI line. */
export function shouldUseLlm(beat: Beat, overBudgetOrState: boolean | BudgetState): boolean {
  if (typeof overBudgetOrState === 'boolean') {
    if (overBudgetOrState) return false;
    // No budget detail given: only the very quiet ambient chatter defaults to
    // templates; everything else is fresh. Cheap, freshness-biased default.
    return beat.priority !== 'ambient' ? true : false;
  }
  return shouldUseLlmForBatch(beat.priority, 1, overBudgetOrState);
}

/** The loudest priority present in a batch (drives the headroom gate). */
export function loudestPriority(beats: readonly Beat[]): BeatPriority {
  let best: BeatPriority = 'ambient';
  for (const b of beats) {
    if (b.priority === 'highlight') return 'highlight';
    if (b.priority === 'normal') best = 'normal';
  }
  return best;
}

/** Apply spend to a BudgetState, returning a new state (immutable update). */
export function chargeTokens(state: BudgetState, tokensUsed: number): BudgetState {
  const spent = state.tokensSpentToday + Math.max(0, tokensUsed);
  return {
    dailyTokenBudget: state.dailyTokenBudget,
    tokensSpentToday: spent,
    overBudget: spent >= state.dailyTokenBudget,
  };
}

/** Reset spend on in-game day rollover (the budget refreshes; never punishes). */
export function rolloverDay(state: BudgetState): BudgetState {
  return {
    dailyTokenBudget: state.dailyTokenBudget,
    tokensSpentToday: 0,
    overBudget: false,
  };
}
